import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import RiskScoreChart from '../components/CommandCenter/RiskScoreChart';
import TopRisksTable from '../components/CommandCenter/TopRisksTable';
import {
    getCommandCenterSummary,
    getSeverityColor
} from '../services/commandCenterService';
import { getAsset, triggerAssetScan } from '../services/assetService';
import './CommandCenter.css';
import DriftSummaryCard from '../components/DriftSummaryCard';
import DriftMetricsCard from '../components/CommandCenter/DriftMetricsCard';
import AssetsWidget from '../components/CommandCenter/AssetsWidget';
import FixTheseFirstWidget from '../components/CommandCenter/FixTheseFirstWidget';
import FirstScansBanner from '../components/CommandCenter/FirstScansBanner';
import OnboardingWizard from '../components/OnboardingWizard';
import { HelpIcon } from '../components/common';
import { useOnboardingState, updateOnboardingState } from '../services/profileService';
import RiskBeeswarm from '../components/viz/RiskBeeswarm';

// Severity breakdown card
function SeverityCard({ vulnCounts }) {
    const severities = [
        { key: 'critical', label: 'Critical', color: 'var(--severity-critical)' },
        { key: 'high', label: 'High', color: 'var(--severity-high)' },
        { key: 'medium', label: 'Medium', color: 'var(--severity-medium)' },
        { key: 'low', label: 'Low', color: 'var(--severity-low)' }
    ];

    return (
        <div className="cm-metric-card cm-severity-card">
            <div className="metric-header">
                <span className="metric-icon">📊</span>
                <span className="metric-label">Vulnerability Breakdown<HelpIcon content="Total vulnerability findings across all monitored assets, grouped by severity level (Critical, High, Medium, Low)." category="context" /></span>
            </div>
            <div className="severity-grid">
                {severities.map(sev => (
                    <div key={sev.key} className="severity-item">
                        <span className="sev-count" style={{ color: sev.color }}>
                            {vulnCounts?.[sev.key] || 0}
                        </span>
                        <span className="sev-label">{sev.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}


// Toggle button for red/blue team view
function TeamToggle({ activeTeam, onChange }) {
    return (
        <div className="team-toggle">
            <button
                className={`toggle-btn ${activeTeam === 'red' ? 'active red' : ''}`}
                onClick={() => onChange('red')}
            >
                🔴 Attack Paths
            </button>
            <button
                className={`toggle-btn ${activeTeam === 'blue' ? 'active blue' : ''}`}
                onClick={() => onChange('blue')}
            >
                🔵 Mitigations
            </button>
        </div>
    );
}

// Prioritized recommendations table row
function RecommendationRow({ rec, index, teamView, onClick }) {
    const content = teamView === 'red' ? rec.attackVector : rec.remediation;

    return (
        <tr className="rec-row" onClick={onClick}>
            <td className="col-rank">
                <span className="rank-badge" style={{ background: getSeverityColor(rec.priority?.toLowerCase()) }}>
                    {index + 1}
                </span>
            </td>
            <td className="col-asset">
                {rec.assetValue}
                {rec._assetCount > 1 && (
                    <span className="rec-multi-asset" title={`Affects ${rec._assetCount} assets`}>
                        +{rec._assetCount - 1}
                    </span>
                )}
            </td>
            <td className="col-vuln">{rec.vulnerability}</td>
            <td className="col-content">{content}</td>
            <td className="col-effort">
                <span className={`effort-badge effort-${rec.effort?.toLowerCase()}`}>
                    {rec.effort}
                </span>
            </td>
        </tr>
    );
}

function CommandCenter() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        summary: {},
        assets: [],
        topRisks: [],
        trends: {},
        meta: {}
    });

    // PRD-68: trajectory window for the AssetsWidget (lifted state so we can
    // re-fetch the summary when the user toggles 7d/30d/90d).
    const [trajectoryWindowDays, setTrajectoryWindowDays] = useState(7);

    // Onboarding wizard state (PRD-64: server-side, gated on profile load)
    const { onboardingState, loading: onboardingLoading, refresh: refreshOnboardingState } = useOnboardingState();
    const [wizardLocallyClosed, setWizardLocallyClosed] = useState(false);
    // Settings "Re-run setup wizard" navigates here with { reopenWizard: true } so an
    // existing-asset user can add another asset — this bypasses the asset-first gate below.
    const forceWizard = !!location.state?.reopenWizard;
    // showWizard is computed further down, once `hasAssets`/`loading`/`error` are in scope
    // (asset-first, load-guarded gate — see below).

    // Aggregated recommendations from all assets
    const [recommendations, setRecommendations] = useState([]);
    const [recsLoading, setRecsLoading] = useState(true);

    // Collapsible sections state
    const [sectionsOpen, setSectionsOpen] = useState({
        trend: true,
        assets: true,
        risks: true,
        recommendations: true,
        distribution: true,
        drift: true,
        fixFirst: true
    });

    // Red/Blue team toggle
    const [teamView, setTeamView] = useState('blue');

    // Quick scan state
    const [scanningAssets, setScanningAssets] = useState(new Set());

    const handleQuickScan = async (assetId, assetValue) => {
        try {
            setScanningAssets(prev => new Set(prev).add(assetId));
            await triggerAssetScan(assetId, 'vuln-scan');
        } catch (error) {
            console.error('Quick scan failed:', error);
        } finally {
            setTimeout(() => {
                setScanningAssets(prev => {
                    const next = new Set(prev);
                    next.delete(assetId);
                    return next;
                });
            }, 3000);
        }
    };

    // PRD-75 Stage 4 (2026-05-24): the "Executive Report" CTA was previously a
    // one-shot PDF generator (inline Claude → @react-pdf → file-saver). It now
    // deep-links to the persisted weekly user-level trend reports on the Reports
    // page so users see the rollups the weekly cron has been writing (PRD-76
    // Phase 1). Persistence-backed beats single-use because the same artifact is
    // already emailed weekly and shareable.
    const handleGenerateExecReport = useCallback(() => {
        navigate('/reports?type=trend-weekly');
    }, [navigate]);

    // Toggle section collapse
    const toggleSection = useCallback((section) => {
        setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
    }, []);

    // Fetch command center data. Re-fetches when the user toggles the
    // AssetsWidget trajectory window (7/30/90).
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await getCommandCenterSummary(trajectoryWindowDays);
                if (result.success) {
                    setData({
                        summary: result.summary || {},
                        assets: result.assets || [],
                        topRisks: result.topRisks || [],
                        trends: result.trends || {},
                        meta: result.meta || {}
                    });
                } else {
                    throw new Error(result.error || 'Failed to load data');
                }
            } catch (err) {
                console.error('Error fetching command center data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [trajectoryWindowDays]);

    // Fetch and aggregate prioritized recommendations from all assets
    useEffect(() => {
        const fetchRecommendations = async () => {
            if (data.assets.length === 0) {
                setRecsLoading(false);
                return;
            }

            try {
                setRecsLoading(true);
                const allRecs = [];

                // Fetch details for each asset (in parallel, limited to top 10)
                const assetPromises = data.assets.slice(0, 10).map(async (asset) => {
                    try {
                        const details = await getAsset(asset.assetId);
                        const recs = details?.enrichment?.aiAnalysis?.prioritizedRecommendations || [];
                        return recs.map(rec => ({
                            ...rec,
                            assetId: asset.assetId,
                            assetValue: asset.value,
                            assetRiskScore: asset.riskScore || 0
                        }));
                    } catch {
                        return [];
                    }
                });

                const results = await Promise.all(assetPromises);
                results.forEach(recs => allRecs.push(...recs));

                // Dedup across assets: same vulnerability name → keep instance from worst-scoring asset
                const dedupMap = new Map();
                for (const rec of allRecs) {
                    const key = rec.vulnerability?.toLowerCase().trim();
                    if (!key) continue;
                    const existing = dedupMap.get(key);
                    if (!existing || (rec.assetRiskScore || 100) < (existing.assetRiskScore || 100)) {
                        const count = existing ? (existing._assetCount || 1) + 1 : 1;
                        dedupMap.set(key, { ...rec, _assetCount: count });
                    } else if (existing) {
                        existing._assetCount = (existing._assetCount || 1) + 1;
                    }
                }
                const uniqueRecs = Array.from(dedupMap.values());

                // Sort: severity tier → effort (low first) → asset risk score (lowest first)
                const priorityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, INFO: 4 };
                const effortOrder = { LOW: 0, MEDIUM: 1, HIGH: 2 };

                uniqueRecs.sort((a, b) => {
                    const aSev = priorityOrder[a.priority?.toUpperCase()] ?? 5;
                    const bSev = priorityOrder[b.priority?.toUpperCase()] ?? 5;
                    if (aSev !== bSev) return aSev - bSev;

                    const aEff = effortOrder[a.effort?.toUpperCase()] ?? 1;
                    const bEff = effortOrder[b.effort?.toUpperCase()] ?? 1;
                    if (aEff !== bEff) return aEff - bEff;

                    return (a.assetRiskScore || 100) - (b.assetRiskScore || 100);
                });

                // Waterfall fill: take top 5
                setRecommendations(uniqueRecs.slice(0, 5));
            } catch (err) {
                console.log('Failed to fetch recommendations:', err);
            } finally {
                setRecsLoading(false);
            }
        };

        if (!loading && data.assets.length > 0) {
            fetchRecommendations();
        }
    }, [loading, data.assets]);

    const { summary, assets, topRisks, trends, meta } = data;

    // Calculate assets at risk (critical + atRisk levels)
    const assetsAtRisk = useMemo(() => {
        return (summary.riskDistribution?.critical || 0) + (summary.riskDistribution?.high || 0);
    }, [summary.riskDistribution]);

    const hasAssets = assets.length > 0;

    // Asset-first, race-proof onboarding-wizard gate. Once ANY asset exists the wizard
    // never shows again — independent of whether the completedAt/dismissedAt PUT landed
    // (removes the last-write-wins clobber that made the popup reappear). The load/error
    // guards ensure hasAssets is trustworthy before we render (no flash on slow loads).
    const showWizard = !onboardingLoading
        && !loading
        && !error
        && !wizardLocallyClosed
        && (forceWizard || (
            !hasAssets
            && onboardingState
            && !onboardingState.completedAt
            && !onboardingState.dismissedAt
        ));

    const handleWizardComplete = () => {
        setWizardLocallyClosed(true);
        // Pull fresh onboardingState so the wizard does not re-open on a state race
        refreshOnboardingState();
        // Refresh command center data
        const refetch = async () => {
            try {
                const result = await getCommandCenterSummary(trajectoryWindowDays);
                if (result.success) {
                    setData({
                        summary: result.summary || {},
                        assets: result.assets || [],
                        topRisks: result.topRisks || [],
                        trends: result.trends || {},
                        meta: result.meta || {}
                    });
                }
            } catch { /* ignore */ }
        };
        refetch();
    };

    return (
        <CleanModernLayout activePage="overview">
            {showWizard && (
                <OnboardingWizard onComplete={handleWizardComplete} />
            )}
            {error ? (
                <div className="cm-error-state">
                    <div className="error-icon">⚠️</div>
                    <h3>Error Loading Data</h3>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()} className="cm-action-btn">
                        Retry
                    </button>
                </div>
            ) : loading ? (
                <div className="cm-loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading command center...</p>
                </div>
            ) : (
                <div className="command-center-content cm-page-content">

                    {/* Page Header */}
                    <header className="cm-page-header">
                        <div className="header-left">
                            <h1 className="page-title">Command Center</h1>
                        </div>
                        <div className="header-actions">
                            <button
                                className="cm-action-btn cm-exec-report-btn"
                                onClick={handleGenerateExecReport}
                                title="View your weekly user-level trend reports"
                            >
                                Weekly Trend Reports
                            </button>
                        </div>
                    </header>

                    {/* PRD-64 WP3 FollowUp Part D — first-15-minute empty-state banner.
                        Gates inside the component; renders null for everyone except a
                        fresh user with assets but no completed scans. */}
                    <FirstScansBanner
                        assets={assets}
                        onboardingState={onboardingState}
                        loading={loading || onboardingLoading}
                        onDismiss={async () => {
                            try {
                                await updateOnboardingState({ firstScansBannerDismissedAt: new Date().toISOString() });
                                refreshOnboardingState();
                            } catch (err) {
                                console.error('Failed to dismiss first-scans banner:', err);
                            }
                        }}
                    />

                    {/* Overview Metrics - 2 cards: At Risk, Vulnerability Breakdown */}
                    <section className="cm-metrics-row">
                        {/* Assets at Risk */}
                        <div className="cm-metric-card">
                            <div className="metric-header">
                                <span className="metric-icon">⚠️</span>
                                <span className="metric-label">Assets at Risk<HelpIcon content="Overall security posture score (0-100). Higher is better. Based on vulnerabilities, exposed services, and configuration issues." category="risk" /></span>
                            </div>
                            <div className="metric-value" style={{ color: 'var(--severity-high)' }}>
                                {assetsAtRisk}
                                <span className="metric-total">/{summary.totalAssets || 0}</span>
                            </div>
                            <div className="metric-breakdown">
                                {summary.assetsByType?.domain || 0} domains • {(summary.assetsByType?.ip || 0) + (summary.assetsByType?.cidr || 0)} IPs
                            </div>
                        </div>

                        {/* Vulnerability Breakdown */}
                        <SeverityCard vulnCounts={summary.totalVulnerabilities} />

                        {/* Drift Events */}
                        <DriftMetricsCard driftSummary={summary.driftSummary} meta={meta?.driftSummary} />
                    </section>

                    {/* Security Trend Section */}
                    <section className="cm-section">
                        <div
                            className="cm-section-header"
                            onClick={() => toggleSection('trend')}
                        >
                            <div className="header-left">
                                <button className={`collapse-btn ${sectionsOpen.trend ? 'open' : ''}`}>
                                    <span className="collapse-icon">▶</span>
                                </button>
                                <span className="header-icon">📈</span>
                                <h2>Security Trend</h2>
                            </div>
                        </div>
                        {sectionsOpen.trend && (
                            <div className="cm-section-content">
                                <RiskScoreChart
                                    data={trends.riskScoreHistory || []}
                                    perAssetHistory={trends.perAssetHistory || null}
                                    showVulnCount={true}
                                />
                            </div>
                        )}
                    </section>

                    {/* Assets Section (PRD-68 \u2014 promoted from bottom slot, now carries
                        trajectory / change / risk-pill columns inherited from the retired
                        Top Movers widget). */}
                    <section className="cm-section">
                        <div
                            className="cm-section-header"
                            onClick={() => toggleSection('assets')}
                        >
                            <div className="header-left">
                                <button className={`collapse-btn ${sectionsOpen.assets ? 'open' : ''}`}>
                                    <span className="collapse-icon">{'\u25B6'}</span>
                                </button>
                                <span className="header-icon">{'\u{1F310}'}</span>
                                <h2>
                                    Assets
                                    <HelpIcon
                                        content="All assets sorted by current risk score (highest first). Trajectory shows the risk-score line over the selected window; Change is the arithmetic delta; Risk pill is the trend direction (Increasing/Decreasing/Stable). Toggle 7d/30d/90d to change the window."
                                        category="context"
                                    />
                                </h2>
                            </div>
                            <div className="header-actions" onClick={e => e.stopPropagation()}>
                                <button
                                    className="section-action"
                                    onClick={() => navigate('/assets')}
                                >
                                    Manage all assets \u2192
                                </button>
                            </div>
                        </div>
                        {sectionsOpen.assets && (
                            <div className="cm-section-content">
                                <AssetsWidget
                                    assets={assets}
                                    total={assets.length}
                                    windowDays={trajectoryWindowDays}
                                    onWindowChange={setTrajectoryWindowDays}
                                    meta={meta?.assetsTrajectory}
                                    onAssetClick={(assetId) => navigate(`/assets/${assetId}#what-changed`)}
                                    onQuickScan={handleQuickScan}
                                    scanningAssets={scanningAssets}
                                />
                            </div>
                        )}
                    </section>

                    {/* Drift Summary Section */}
                    <section className="cm-section">
                        <div
                            className="cm-section-header"
                            onClick={() => toggleSection('drift')}
                        >
                            <div className="header-left">
                                <button className={`collapse-btn ${sectionsOpen.drift ? 'open' : ''}`}>
                                    <span className="collapse-icon">&#x25B6;</span>
                                </button>
                                <span className="header-icon">&#x1F504;</span>
                                <h2>Drift Summary<HelpIcon content="Drift detection monitors your assets for unexpected changes. Stable = no changes. Changed = modifications detected. Alert = policy violation." category="context" /></h2>
                            </div>
                        </div>
                        {sectionsOpen.drift && (
                            <div className="cm-section-content">
                                <DriftSummaryCard
                                    assets={assets}
                                    driftSummary={summary.driftSummary}
                                    driftCoverage={summary.driftCoverage}
                                    meta={meta?.driftSummary}
                                />
                            </div>
                        )}
                    </section>

                    {/* Prioritized Recommendations Section */}
                    <section className="cm-section">
                        <div
                            className="cm-section-header"
                            onClick={() => toggleSection('recommendations')}
                        >
                            <div className="header-left">
                                <button className={`collapse-btn ${sectionsOpen.recommendations ? 'open' : ''}`}>
                                    <span className="collapse-icon">▶</span>
                                </button>
                                <span className="header-icon">🎯</span>
                                <h2>Prioritized Recommendations</h2>
                            </div>
                            <div className="header-actions" onClick={e => e.stopPropagation()}>
                                <TeamToggle activeTeam={teamView} onChange={setTeamView} />
                            </div>
                        </div>
                        {sectionsOpen.recommendations && (
                            <div className="cm-section-content">
                                {recsLoading ? (
                                    <div className="table-loading">Loading recommendations...</div>
                                ) : recommendations.length > 0 ? (
                                    <div className="cm-table-wrapper">
                                        <table className="cm-table recommendations-table">
                                            <thead>
                                                <tr>
                                                    <th className="col-rank">#</th>
                                                    <th className="col-asset">Asset</th>
                                                    <th className="col-vuln">Vulnerability</th>
                                                    <th className="col-content">
                                                        {teamView === 'red' ? 'Attack Path' : 'Mitigation'}
                                                    </th>
                                                    <th className="col-effort">Effort</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recommendations.map((rec, i) => (
                                                    <RecommendationRow
                                                        key={`${rec.assetId}-${i}`}
                                                        rec={rec}
                                                        index={i}
                                                        teamView={teamView}
                                                        onClick={() => navigate(`/assets/${rec.assetId}#recommendations`)}
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="cm-empty-state">
                                        <span className="empty-icon">✓</span>
                                        <p>No prioritized recommendations available</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>

                    {/* PRD-130 WP3: CTEM "Fix These First" prioritized alarms */}
                    <section className="cm-section">
                        <div
                            className="cm-section-header"
                            onClick={() => toggleSection('fixFirst')}
                        >
                            <div className="header-left">
                                <button className={`collapse-btn ${sectionsOpen.fixFirst ? 'open' : ''}`}>
                                    <span className="collapse-icon">▶</span>
                                </button>
                                <span className="header-icon">🛠️</span>
                                <h2>Fix These First</h2>
                                <HelpIcon
                                    content="Your open alarms ranked by real-world risk — exploitability (KEV/EPSS), asset criticality, blast radius and recency — so you know the 3 to fix first."
                                    category="drift"
                                />
                            </div>
                        </div>
                        {sectionsOpen.fixFirst && (
                            <div className="cm-section-content">
                                <FixTheseFirstWidget />
                            </div>
                        )}
                    </section>

                    {/* Two Column Layout */}
                    <div className="cm-two-column">
                        {/* Top Risks */}
                        <section className="cm-section cm-column-main">
                            <div
                                className="cm-section-header"
                                onClick={() => toggleSection('risks')}
                            >
                                <div className="header-left">
                                    <button className={`collapse-btn ${sectionsOpen.risks ? 'open' : ''}`}>
                                        <span className="collapse-icon">▶</span>
                                    </button>
                                    <h2>Top Risks</h2>
                                </div>
                            </div>
                            {sectionsOpen.risks && (
                                <div className="cm-section-content">
                                    <TopRisksTable risks={topRisks} limit={10} meta={meta?.topRisks} />
                                </div>
                            )}
                        </section>

                        {/* Risk Distribution */}
                        <section className="cm-section cm-column-side">
                            <div
                                className="cm-section-header"
                                onClick={() => toggleSection('distribution')}
                            >
                                <div className="header-left">
                                    <button className={`collapse-btn ${sectionsOpen.distribution ? 'open' : ''}`}>
                                        <span className="collapse-icon">▶</span>
                                    </button>
                                    <h2>Asset Risk Distribution</h2>
                                </div>
                            </div>
                            {sectionsOpen.distribution && (
                                <div className="cm-section-content">
                                    {assets.length > 0 ? (
                                        <div className="risk-beeswarm-wrap">
                                            <RiskBeeswarm
                                                data={assets.map(a => ({
                                                    id: a.assetId || a.value,
                                                    label: a.value,
                                                    riskScore: a.baselineRiskScore != null ? a.baselineRiskScore : a.riskScore
                                                }))}
                                                width={320}
                                                height={130}
                                                title="Asset Risk Distribution"
                                                desc={`Each dot is one of your ${assets.length} assets, positioned by current risk score (0=healthy, 100=critical). Hover for asset name.`}
                                            />
                                            <div className="risk-beeswarm-legend">
                                                {[
                                                    { key: 'critical', label: 'Critical', cls: 'critical' },
                                                    { key: 'high', label: 'High', cls: 'at-risk' },
                                                    { key: 'medium', label: 'Medium', cls: 'warning' },
                                                    { key: 'low', label: 'Low', cls: 'info' },
                                                    { key: 'healthy', label: 'Healthy', cls: 'healthy' }
                                                ].map(({ key, label, cls }) => {
                                                    const count = summary.riskDistribution?.[key] || 0;
                                                    return (
                                                        <span className="risk-beeswarm-legend-item" key={key}>
                                                            <span className={`dist-dot ${cls}`}></span>
                                                            <span className="legend-label">{label}</span>
                                                            <span className="legend-count">{count}</span>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="cm-empty-hint">Add an asset to see your risk distribution.</p>
                                    )}
                                </div>
                            )}
                        </section>
                    </div>

                </div>
            )}
        </CleanModernLayout>
    );
}

export default CommandCenter;
